import { useEffect, useMemo, useState, type ReactNode } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Lock, Search, Sparkles } from 'lucide-react';
import api from '@/lib/axios';
import { cn } from '@/lib/utils';
import { formatearFechaHora } from '@/lib/tareas';
import { useEntidadesLista, useEntidadesResumen } from '@/hooks/useTareaEntidades';
import type { EntidadItem, EntidadTipoTarea, TipoEntidadBuscable } from '@/types/tareas';

const campo = 'w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white';
const DEBOUNCE_MS = 300;
const MIN_LETRAS_PACIENTE = 2;
const PACIENTES_MAX = 8;

export interface RelacionSeleccion {
  entidadTipo: EntidadTipoTarea;
  entidadId: string;
  label: string;
}

const TABS: { tipo: TipoEntidadBuscable; etiqueta: string }[] = [
  { tipo: 'cita', etiqueta: 'Citas' },
  { tipo: 'consulta', etiqueta: 'Consultas' },
  { tipo: 'presupuesto', etiqueta: 'Presupuestos' },
];

const legible = (v?: string | null) => (v ? v.replace(/_/g, ' ').replace(/^./, c => c.toUpperCase()) : '');
const fechaCorta = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) : '';

function useDebounce<T>(valor: T, ms = DEBOUNCE_MS): T {
  const [retrasado, setRetrasado] = useState(valor);
  useEffect(() => {
    const t = setTimeout(() => setRetrasado(valor), ms);
    return () => clearTimeout(t);
  }, [valor, ms]);
  return retrasado;
}

function aSeleccion(item: EntidadItem): RelacionSeleccion {
  const label = item.tipo === 'presupuesto'
    ? item.titulo ?? 'Presupuesto'
    : `${item.tipo === 'consulta' ? 'Consulta' : 'Cita'} · ${fechaCorta(item.fecha)}`;
  return { entidadTipo: item.tipo, entidadId: item.id, label };
}

function nombrePaciente(p: any): string {
  return [p?.nombre, p?.apellidoPaterno, p?.apellidoMaterno].filter(Boolean).join(' ');
}

/**
 * Selector de una relación para la tarea: busca un paciente y luego elige él mismo, una sugerencia
 * o un registro de sus citas, consultas o presupuestos (con búsqueda y "cargar más").
 * `fechaReferencia` (p. ej. el vencimiento de la tarea) ordena las sugerencias por cercanía.
 */
export function RelacionesPicker({
  onAgregar, children, fechaReferencia,
}: { onAgregar: (r: RelacionSeleccion) => void; children: ReactNode; fechaReferencia?: string | null }) {
  const [abierto, setAbierto] = useState(false);
  const [busquedaPaciente, setBusquedaPaciente] = useState('');
  const [paciente, setPaciente] = useState<{ id: string; nombre: string } | null>(null);

  const cerrar = () => { setAbierto(false); setPaciente(null); setBusquedaPaciente(''); };
  const elegir = (r: RelacionSeleccion) => { onAgregar(r); cerrar(); };

  return (
    <Popover.Root open={abierto} onOpenChange={v => (v ? setAbierto(true) : cerrar())}>
      <Popover.Trigger asChild>{children}</Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={4}
          className="z-[70] w-[26rem] max-w-[calc(100vw-2rem)] max-h-[32rem] overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-lg p-3"
        >
          {!paciente
            ? <BuscarPaciente busqueda={busquedaPaciente} onBusqueda={setBusquedaPaciente} onElegir={setPaciente} activo={abierto} />
            : <DetallePaciente paciente={paciente} fechaReferencia={fechaReferencia} onVolver={() => setPaciente(null)} onElegir={elegir} />}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

/* ── Paso 1: paciente ────────────────────────────────────────────────────── */

function BuscarPaciente({
  busqueda, onBusqueda, onElegir, activo,
}: { busqueda: string; onBusqueda: (v: string) => void; onElegir: (p: { id: string; nombre: string }) => void; activo: boolean }) {
  const termino = useDebounce(busqueda.trim());
  const { data: pacientes = [], isFetching } = useQuery<any[]>({
    queryKey: ['tareas', 'buscar-pacientes', termino],
    queryFn: () => api.get('/api/pacientes', { params: { search: termino, page: 1, limit: PACIENTES_MAX } })
      .then(r => (Array.isArray(r.data) ? r.data : r.data?.data ?? [])),
    enabled: activo && termino.length >= MIN_LETRAS_PACIENTE,
    staleTime: 30_000,
  });

  return (
    <>
      <div className="relative">
        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          autoFocus
          value={busqueda}
          onChange={e => onBusqueda(e.target.value)}
          placeholder="Buscar paciente (mín. 2 letras)"
          className={cn(campo, 'pl-8')}
        />
      </div>
      <div className="mt-1">
        {isFetching && <p className="p-2 text-sm text-gray-400">Buscando…</p>}
        {pacientes.map(p => (
          <button
            type="button"
            key={p.id}
            className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-gray-50 text-sm text-text-primary truncate"
            onClick={() => onElegir({ id: p.id, nombre: nombrePaciente(p) })}
          >
            {nombrePaciente(p)}
          </button>
        ))}
        {!isFetching && termino.length >= MIN_LETRAS_PACIENTE && !pacientes.length && (
          <p className="p-2 text-sm text-gray-400">Sin resultados.</p>
        )}
      </div>
    </>
  );
}

/* ── Paso 2: qué ligar del paciente ──────────────────────────────────────── */

function DetallePaciente({
  paciente, fechaReferencia, onVolver, onElegir,
}: { paciente: { id: string; nombre: string }; fechaReferencia?: string | null; onVolver: () => void; onElegir: (r: RelacionSeleccion) => void }) {
  const { data: resumen, isLoading, error } = useEntidadesResumen(paciente.id, fechaReferencia);
  const [tab, setTab] = useState<TipoEntidadBuscable | null>(null);
  const [busqueda, setBusqueda] = useState('');
  const termino = useDebounce(busqueda.trim());

  // Pestaña inicial: la primera con registros (citas, luego consultas, luego presupuestos).
  const tabInicial = useMemo<TipoEntidadBuscable>(() => {
    if (!resumen) return 'cita';
    return TABS.find(t => (resumen.conteos[t.tipo] ?? 0) > 0)?.tipo ?? 'cita';
  }, [resumen]);
  const tabActiva = tab ?? tabInicial;

  const pestanas = TABS.filter(t => resumen?.conteos[t.tipo] !== null);
  const sinAcceso = (error as any)?.response?.status === 403;

  return (
    <>
      <button type="button" onClick={onVolver} className="text-xs text-primary-600 hover:underline px-1 py-0.5">
        ← Cambiar paciente
      </button>
      <button
        type="button"
        onClick={() => onElegir({ entidadTipo: 'paciente', entidadId: paciente.id, label: paciente.nombre })}
        className="mt-1 w-full text-left px-3 py-2 rounded-lg border border-primary-200 bg-primary-50 text-sm font-medium text-primary-800 hover:bg-primary-100 truncate"
      >
        Ligar al paciente: {paciente.nombre}
      </button>

      {isLoading && <div className="py-6 flex justify-center text-gray-400"><Loader2 size={18} className="animate-spin" /></div>}

      {error && (
        <div className="py-6 text-center">
          <Lock className="mx-auto text-gray-300" size={26} />
          <p className="mt-2 text-sm text-text-secondary">
            {sinAcceso ? 'No tienes acceso a los registros de este paciente.' : 'No se pudieron cargar los registros.'}
          </p>
        </div>
      )}

      {resumen && (
        <>
          {resumen.sugeridos.length > 0 && (
            <section className="mt-3">
              <h4 className="flex items-center gap-1.5 px-1 mb-1 text-[11px] font-semibold uppercase tracking-wide text-text-secondary">
                <Sparkles size={12} /> Sugeridos
              </h4>
              {resumen.sugeridos.map(s => <FilaEntidad key={`${s.tipo}-${s.id}`} item={s} onElegir={() => onElegir(aSeleccion(s))} />)}
            </section>
          )}

          <div className="mt-3 flex gap-1 border-b border-gray-100" role="tablist">
            {pestanas.map(t => (
              <button
                key={t.tipo}
                type="button"
                role="tab"
                aria-selected={tabActiva === t.tipo}
                onClick={() => { setTab(t.tipo); setBusqueda(''); }}
                className={cn(
                  'px-2.5 py-1.5 text-sm font-medium border-b-2 -mb-px',
                  tabActiva === t.tipo ? 'border-primary-500 text-primary-700' : 'border-transparent text-text-secondary hover:text-text-primary',
                )}
              >
                {t.etiqueta} <span className="text-[11px] text-text-secondary">({resumen.conteos[t.tipo]})</span>
              </button>
            ))}
          </div>

          <ListaEntidades
            key={tabActiva}
            pacienteId={paciente.id}
            tipo={tabActiva}
            busqueda={busqueda}
            termino={termino}
            onBusqueda={setBusqueda}
            onElegir={item => onElegir(aSeleccion(item))}
          />
        </>
      )}
    </>
  );
}

function ListaEntidades({
  pacienteId, tipo, busqueda, termino, onBusqueda, onElegir,
}: {
  pacienteId: string; tipo: TipoEntidadBuscable; busqueda: string; termino: string;
  onBusqueda: (v: string) => void; onElegir: (item: EntidadItem) => void;
}) {
  const { data, isLoading, isFetching, hasNextPage, fetchNextPage, isFetchingNextPage } = useEntidadesLista(pacienteId, tipo, termino);
  const items = data?.pages.flatMap(p => p.items) ?? [];
  const total = data?.pages[0]?.total ?? 0;

  return (
    <div className="mt-2">
      <div className="relative">
        <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          value={busqueda}
          onChange={e => onBusqueda(e.target.value)}
          placeholder="Buscar por fecha (30/07), médico, motivo…"
          className={cn(campo, 'pl-8 py-1.5')}
          aria-label="Buscar en la lista"
        />
      </div>

      <div className="mt-1">
        {isLoading && <div className="py-4 flex justify-center text-gray-400"><Loader2 size={16} className="animate-spin" /></div>}
        {!isLoading && items.length === 0 && (
          <p className="p-3 text-sm text-gray-400">{termino ? 'Sin coincidencias.' : 'No hay registros.'}</p>
        )}
        {items.map(item => <FilaEntidad key={item.id} item={item} onElegir={() => onElegir(item)} />)}

        {hasNextPage && (
          <button
            type="button"
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
            className="w-full mt-1 py-1.5 rounded-lg text-sm text-primary-600 hover:bg-primary-50 disabled:opacity-60"
          >
            {isFetchingNextPage ? 'Cargando…' : `Mostrar más (${total - items.length} restantes)`}
          </button>
        )}
        {isFetching && !isLoading && !isFetchingNextPage && <p className="px-2 pt-1 text-[11px] text-gray-400">Actualizando…</p>}
      </div>
    </div>
  );
}

/** Una fila debe bastar para reconocer el registro: fecha y hora, médico, estado y motivo/diagnóstico/título. */
function FilaEntidad({ item, onElegir }: { item: EntidadItem; onElegir: () => void }) {
  const esPresupuesto = item.tipo === 'presupuesto';
  const principal = esPresupuesto ? item.titulo ?? 'Presupuesto' : formatearFechaHora(item.fecha);
  const secundario = [
    esPresupuesto ? fechaCorta(item.fecha) : null,
    item.medico,
    esPresupuesto ? null : item.titulo,
    item.tipo === 'cita' ? item.detalle : null,
    esPresupuesto && item.detalle !== null ? `Total ${Number(item.detalle).toLocaleString('es-MX', { style: 'currency', currency: 'MXN' })}` : null,
  ].filter(Boolean).join(' · ');

  return (
    <button type="button" onClick={onElegir} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-gray-50">
      <span className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-text-primary truncate">{principal}</span>
        {item.estado && (
          <span className="flex-shrink-0 px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 text-[11px]">{legible(item.estado)}</span>
        )}
      </span>
      <span className="block text-xs text-text-secondary truncate">{secundario || 'Sin más datos'}</span>
      {item.motivoSugerencia && <span className="block text-[11px] text-primary-600">{item.motivoSugerencia}</span>}
    </button>
  );
}
